import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Container, Typography, Paper } from '@material-ui/core';
import { SideNavbar } from '../nav/SideNavbar';
import { ThemeToggle } from '../theme/ThemeToggle';
import { TextDecrypt } from '../content/TextDecrypt';

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: '100vh',
    width: '100%',
    paddingTop: theme.spacing(10),
    paddingBottom: theme.spacing(8),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      paddingTop: theme.spacing(12),
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
    },
  },
  container: {
    maxWidth: 880,
    margin: '0 auto',
  },
  hero: {
    textAlign: 'center',
    marginBottom: theme.spacing(5),
  },
  brand: {
    fontFamily: 'Roboto Mono, monospace',
    fontWeight: 700,
    letterSpacing: '0.25rem',
    fontSize: '2.1rem',
    lineHeight: 1.15,
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    [theme.breakpoints.up('md')]: {
      fontSize: '3.25rem',
      letterSpacing: '0.4rem',
    },
  },
  subtitle: {
    fontFamily: 'Roboto Mono, monospace',
    fontWeight: 500,
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
    letterSpacing: '0.15rem',
    textTransform: 'uppercase',
    fontSize: '0.95rem',
  },
  updatedChip: {
    display: 'inline-block',
    marginTop: theme.spacing(2),
    padding: `${theme.spacing(0.5)}px ${theme.spacing(1.5)}px`,
    borderRadius: 999,
    border: `1px solid ${theme.palette.primary.main}`,
    color: theme.palette.primary.main,
    fontFamily: 'Roboto Mono, monospace',
    fontSize: '0.8rem',
  },
  notice: {
    maxWidth: 720,
    margin: `${theme.spacing(3)}px auto 0`,
    padding: theme.spacing(2),
    borderRadius: 12,
    border: `1px solid ${theme.palette.secondary.main}`,
    color: theme.palette.text.secondary,
    fontSize: '0.85rem',
    lineHeight: 1.6,
  },
  intro: {
    textAlign: 'center',
    color: theme.palette.text.secondary,
    lineHeight: 1.7,
    maxWidth: 680,
    margin: `0 auto ${theme.spacing(5)}px`,
    fontSize: '1rem',
  },
  card: {
    position: 'relative',
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    borderRadius: 16,
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(255,255,255,0.03)'
        : 'rgba(0,0,0,0.02)',
    border: `1px solid ${
      theme.palette.type === 'dark'
        ? 'rgba(255,255,255,0.08)'
        : 'rgba(0,0,0,0.08)'
    }`,
    transition: 'transform 0.25s ease, box-shadow 0.25s ease',
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: `0 10px 30px -12px ${theme.palette.primary.main}55`,
    },
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(4),
    },
  },
  sectionHeader: {
    fontFamily: 'Roboto Mono, monospace',
    fontWeight: 700,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1.5),
    fontSize: '1.15rem',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  sectionIndex: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 28,
    height: 28,
    padding: '0 6px',
    borderRadius: 8,
    fontSize: '0.85rem',
    color: theme.palette.type === 'dark' ? '#111' : '#fff',
    backgroundColor: theme.palette.secondary.main,
  },
  title: {
    fontWeight: 700,
    fontSize: '1.05rem',
    marginBottom: theme.spacing(0.5),
  },
  description: {
    color: theme.palette.text.secondary,
    lineHeight: 1.7,
    whiteSpace: 'pre-line',
    fontSize: '0.95rem',
  },
  child: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(1.5),
    paddingLeft: theme.spacing(1),
  },
  bullet: {
    marginTop: 9,
    minWidth: 8,
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
  },
  childTitle: {
    fontWeight: 700,
    fontSize: '0.95rem',
  },
  childDescription: {
    color: theme.palette.text.secondary,
    lineHeight: 1.6,
    whiteSpace: 'pre-line',
    fontSize: '0.9rem',
  },
  contactCard: {
    marginTop: theme.spacing(4),
    padding: theme.spacing(3),
    borderRadius: 16,
    textAlign: 'center',
    border: `1px solid ${theme.palette.secondary.main}`,
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(236,112,76,0.08)'
        : 'rgba(236,112,76,0.06)',
  },
  contactEmail: {
    color: theme.palette.secondary.main,
    fontFamily: 'Roboto Mono, monospace',
    fontWeight: 700,
    textDecoration: 'none',
  },
}));

/**
 * Reusable, responsive layout for legal / policy pages (Privacy Policy, ToS...).
 * Renders a themed hero, an optional notice/intro and a list of section cards.
 * Defensive against malformed section data (e.g. sparse arrays with undefined
 * entries) so a single bad entry can never crash the whole page.
 */
export const LegalPage = ({
  brand,
  subtitle,
  lastUpdated,
  notice,
  intro,
  sections = [],
  contactEmail,
}) => {
  const classes = useStyles();

  const safeSections = (sections || []).filter(Boolean);

  return (
    <>
      <SideNavbar />
      <ThemeToggle />

      <div className={classes.root}>
        <Container className={classes.container} disableGutters>
          <div className={classes.hero}>
            <Typography component="h1" className={classes.brand}>
              <TextDecrypt text={brand} />
            </Typography>
            {subtitle ? (
              <Typography className={classes.subtitle}>{subtitle}</Typography>
            ) : null}
            {lastUpdated ? (
              <span className={classes.updatedChip}>
                Last updated: {lastUpdated}
              </span>
            ) : null}
            {notice ? (
              <Typography className={classes.notice}>{notice}</Typography>
            ) : null}
          </div>

          {intro ? (
            <Typography className={classes.intro}>{intro}</Typography>
          ) : null}

          {safeSections.map((section, index) => {
            const heading = section.header || section.title;
            const hasSubtitle = Boolean(section.header && section.title);
            const children = (section.child || []).filter(Boolean);

            return (
              <Paper
                key={section.id ?? index}
                elevation={0}
                className={classes.card}
              >
                <Typography component="h2" className={classes.sectionHeader}>
                  <span className={classes.sectionIndex}>{index + 1}</span>
                  {heading}
                </Typography>

                {hasSubtitle ? (
                  <Typography className={classes.title}>
                    {section.title}
                  </Typography>
                ) : null}

                {section.description ? (
                  <Typography className={classes.description}>
                    {section.description}
                  </Typography>
                ) : null}

                {children.map((item, i) => (
                  <div key={item.id ?? i} className={classes.child}>
                    <span className={classes.bullet} />
                    <div>
                      {item.title ? (
                        <Typography className={classes.childTitle}>
                          {item.title}
                        </Typography>
                      ) : null}
                      {item.description ? (
                        <Typography className={classes.childDescription}>
                          {item.description}
                        </Typography>
                      ) : null}
                    </div>
                  </div>
                ))}
              </Paper>
            );
          })}

          {contactEmail ? (
            <Paper elevation={0} className={classes.contactCard}>
              <Typography className={classes.title} style={{ marginBottom: 8 }}>
                Questions about this policy?
              </Typography>
              <a
                className={classes.contactEmail}
                href={`mailto:${contactEmail}`}
              >
                {contactEmail}
              </a>
            </Paper>
          ) : null}
        </Container>
      </div>
    </>
  );
};
